// Stories index — renders s.root.storiesTree (from fetchStoriesTree).
// Groups by parent epic; each story card shows task / bug rollups +
// progress. Click opens the story in TicketDetailModal.

import { getSDK } from '../../../shared/functions/sdk.js'

const _openStoryById = async (root, s) => {
  if (!root || typeof root.update !== 'function') return
  // Optimistic open so the modal appears instantly.
  root.update({ selectedTicket: { id: s.id, external_id: `STORY#${s.storyKey}`, title: s.title, issueType: 'user-story', body: '' } })
  if (!s.id) return
  // migrated 2026-04-27 to SDK route workspaceProject.tickets.get
  try {
    const t = await getSDK().execute('workspaceProject.tickets', 'get', { id: s.id })
    if (!t) return
    root.update({ selectedTicket: {
      ...t,
      columnKey: t.column_key,
      issueType: t.issue_type || 'user-story',
      epicName: t.epic_name,
      assigneeEmail: t.assignee_email,
      reporterEmail: t.reporter_email,
      updatedAt: t.updated_at,
      createdAt: t.created_at
    } })
  } catch (_) {}
}

export const TicketsStoriesMain = {
  extends: 'AppMain',

  Header: { extends: 'TicketsHeader' },

  Body: {
    overflow: 'auto',

    Hero: {
      extends: 'PageHero',
      eyebrow: 'ROADMAP · USER STORIES',
      headline: 'Stories',
      lede: (el, s) => {
        const tree = s.root.storiesTree || []
        if (!tree.length) return 'Loading stories from public.tickets…'
        const tasks = tree.reduce((a, x) => a + x.tasksTotal, 0)
        const bugs = tree.reduce((a, x) => a + x.bugsTotal, 0)
        return `${tree.length} stories across ${new Set(tree.map(x => x.epicKey)).size} epics · ${tasks} tasks + ${bugs} bugs.`
      },

      Stats: {
        flow: 'x',
        gap: 'B',
        marginTop: 'Z',
        flexWrap: 'wrap',
        childExtends: 'TicketsEpicHeroStat',
        childrenAs: 'state',
        children: (el, s) => {
          const tree = s.root.storiesTree || []
          const tasks = tree.reduce((a, x) => a + x.tasksTotal, 0)
          const bugs = tree.reduce((a, x) => a + x.bugsTotal, 0)
          const total = tasks + bugs
          const done = tree.reduce((a, x) => a + x.tasksDone + x.bugsDone, 0)
          return [
            { label: 'stories', color: 'cyan', value: tree.length },
            { label: 'tasks', color: 'magenta', value: tasks },
            { label: 'bugs', color: 'red', value: bugs },
            { label: '% done', color: 'green', value: total ? Math.round((done / total) * 100) : 0 }
          ]
        }
      }
    },

    Groups: {
      flow: 'y',
      gap: 'C',
      padding: 'C B1 D B1',
      maxWidth: 'L+L',
      width: '100%',
      margin: '0 auto',

      childExtends: 'TicketsStoriesEpicGroup',
      childrenAs: 'state',
      children: (el, s) => {
        // Collapse the flat list into {epicKey, epicTitle, stories[]}
        // groups, preserving the input order (already EPIC_ORDER-first).
        const tree = s.root.storiesTree || []
        const seen = new Map()
        for (const s of tree) {
          if (!seen.has(s.epicKey)) seen.set(s.epicKey, { epicKey: s.epicKey, epicTitle: s.epicTitle, stories: [] })
          seen.get(s.epicKey).stories.push(s)
        }
        return Array.from(seen.values())
      }
    },

    Empty: {
      hide: (el, s) => (s.root.storiesTree || []).length > 0,
      flow: 'y',
      align: 'center center',
      gap: 'Z1',
      padding: 'D',
      color: 'caption',
      Text: {
        tag: 'p',
        text: 'No stories in public.tickets. Run seed-tickets-from-md.mjs to populate.'
      }
    }
  }
}

// One epic bucket on the stories page — header line + card grid of its
// stories.
export const TicketsStoriesEpicGroup = {
  flow: 'y',
  gap: 'A',

  Head: {
    flow: 'x',
    gap: 'V',
    align: 'baseline left',
    paddingBottom: 'V',
    borderBottomWidth: 'V',
    borderBottomStyle: 'solid',
    borderBottomColor: 'line',

    EpicTitle: {
      tag: 'h2',
      fontSize: 'B',
      fontWeight: '700',
      color: 'title',
      letterSpacing: '-.02em',
      margin: '0',
      text: (el, s) => s.epicTitle || s.epicKey?.toUpperCase() || '?'
    },
    Count: {
      tag: 'span',
      fontSize: 'Y1',
      color: 'caption',
      fontWeight: '600',
      text: (el, s) => `${(s.stories || []).length} stor${(s.stories || []).length === 1 ? 'y' : 'ies'}`
    }
  },

  Grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: 'A',

    childExtends: 'TicketsStoryCard',
    childrenAs: 'state',
    children: (el, s) => s.stories || []
  }
}

export const TicketsStoryCard = {
  tag: 'div',
  flow: 'y',
  gap: 'V',
  padding: 'A',
  background: 'overlay',
  round: 'A',
  borderWidth: 'V',
  borderStyle: 'solid',
  borderColor: 'line',
  cursor: 'pointer',
  transition: 'border-color 0.15s ease',
  ':hover': { borderColor: 'title' },
  onClick: (e, el, s) => {
    _openStoryById(s.root || el.getRoot(), s)
  },

  Head: {
    flow: 'x',
    align: 'center space-between',
    gap: 'V',

    Title: {
      tag: 'span',
      fontSize: 'Y1',
      fontWeight: '700',
      color: 'title',
      text: (el, s) => s.title || s.storyKey || '?'
    },
    DonePct: {
      tag: 'span',
      flexShrink: '0',
      fontSize: 'Y2',
      fontWeight: '700',
      color: (el, s) => (s.donePct || 0) >= 100 ? 'green' : 'caption',
      text: (el, s) => `${s.donePct || 0}%`
    }
  },

  Key: {
    tag: 'span',
    fontSize: 'Y2',
    color: 'caption',
    fontWeight: '600',
    letterSpacing: '.06em',
    textTransform: 'uppercase',
    text: (el, s) => s.storyKey || ''
  },

  Counts: {
    flow: 'x',
    gap: 'Z',
    marginTop: 'V',
    flexWrap: 'wrap',

    Tasks: {
      hide: (el, s) => !s.tasksTotal,
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      text: (el, s) => `${s.tasksDone}/${s.tasksTotal} tasks`
    },
    Bugs: {
      hide: (el, s) => !s.bugsTotal,
      tag: 'span',
      fontSize: 'Y2',
      color: 'red',
      text: (el, s) => `${s.bugsDone}/${s.bugsTotal} bugs`
    },
    EmptyNote: {
      hide: (el, s) => (s.tasksTotal + s.bugsTotal) > 0,
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      fontStyle: 'italic',
      text: 'no tasks yet'
    }
  },

  Progress: {
    hide: (el, s) => !(s.tasksTotal + s.bugsTotal),
    height: '4px',
    round: 'V',
    background: 'line',
    overflow: 'hidden',
    marginTop: 'V',

    Bar: {
      height: '100%',
      round: 'V',
      background: (el, s) => (s.donePct || 0) >= 100 ? 'var(--color-green)' : 'var(--color-cyan)',
      width: (el, s) => `${s.donePct || 0}%`,
      transition: 'width 0.25s ease'
    }
  }
}
